import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import Toast from '../components/Toast';
import './Login.css';

const Login = () => {
  const navigate = useNavigate();
  const { cart } = useCart();
  const [step, setStep] = useState('phone'); // 'phone' or 'otp'
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [toast, setToast] = useState(null);
  const [timer, setTimer] = useState(0);

  // Check if already logged in
  useEffect(() => {
    const user = localStorage.getItem('user_phone');
    if (user) {
      navigate('/');
    }
  }, [navigate]);

  // Timer for OTP resend
  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  const showNotification = (message, type = 'success') => {
    if (window.cordova && window.plugins && window.plugins.toast) {
      window.plugins.toast.show(message, 'long', 'center');
    } else {
      setToast({ message, type });
    }
  };

  const handleCloseToast = () => setToast(null);

  const sendOTP = async () => {
    if (!/^\d{10}$/.test(phone)) {
      showNotification('Please enter a valid 10-digit phone number', 'error');
      return;
    }

    setIsProcessing(true);

    try {
      // Call backend to generate and send SMS
      const response = await fetch('https://panel.supplebakes.com/order/send-otp.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          phone: phone
        })
      });

      const data = await response.json();

      if (data.success) {
        setStep('otp');
        setTimer(30);
        setIsProcessing(false);
        showNotification('OTP sent successfully!');
      } else {
        console.error('SMS Failed:', data.message);
        setIsProcessing(false);
        showNotification(data.message || 'Failed to send OTP', 'error');
      }

    } catch (error) {
      console.error('Error sending OTP:', error);
      setIsProcessing(false);
      showNotification('Network error. Please try again.', 'error');
    }
  };

  const verifyOTP = async () => {
    if (otp.length !== 6) {
      showNotification('Please enter a valid 6-digit OTP', 'error');
      return;
    }

    setIsProcessing(true);

    try {
      // Verify OTP with backend
      const response = await fetch('https://panel.supplebakes.com/order/verify-otp.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          phone: phone,
          otp: otp
        })
      });

      const data = await response.json();

      if (data.success) {
        localStorage.setItem('user_phone', phone);
        showNotification('Login Successful!');
        setTimeout(() => {
          if (cart.items.length > 0) {
            navigate('/checkout');
          } else {
            navigate('/');
          }
        }, 1000);
      } else {
        showNotification(data.message || 'Invalid OTP', 'error');
        setIsProcessing(false);
      }
    } catch (error) {
      console.error('Verification Error:', error);
      showNotification('Verification failed. Please try again.', 'error');
      setIsProcessing(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card glass-effect">
        <div className="login-header">
          <h1 className="login-title">Welcome Back</h1>
          <p className="login-subtitle">Login with your phone number to continue</p>
        </div>

        <div className="login-form">
          {step === 'phone' ? (
            <div className="form-group">
              <label className="form-label">Phone Number</label>
              <div className="phone-input-wrapper">
                <span className="country-code">+91</span>
                <input
                  type="tel"
                  className="form-input phone-input"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  placeholder="Enter 10 digit number"
                  maxLength="10"
                />
              </div>
              <button 
                className="submit-btn btn-primary ripple" 
                onClick={sendOTP}
                disabled={isProcessing || phone.length < 10}
              >
                {isProcessing ? 'Sending...' : 'Get OTP'}
              </button>
            </div>
          ) : (
            <div className="form-group">
              <label className="form-label">Enter OTP</label>
              <input
                type="text"
                className="form-input otp-input"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="------"
                maxLength="6"
              />
              <div className="otp-actions">
                <button 
                  className="resend-btn" 
                  onClick={sendOTP} 
                  disabled={timer > 0 || isProcessing}
                >
                  {timer > 0 ? `Resend in ${timer}s` : 'Resend OTP'}
                </button>
              </div>
              <button 
                className="submit-btn btn-primary ripple" 
                onClick={verifyOTP}
                disabled={otp.length < 6}
              >
                Verify & Login
              </button>
              <button className="back-link" onClick={() => setStep('phone')}>
                Change Phone Number
              </button>
            </div>
          )}
        </div>
      </div>
      
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={handleCloseToast}
        />
      )}
    </div>
  );
};

export default Login;
